import { apiClient } from './client';

/**
 * Tableau de pilotage temps réel — /api/dashboard-live/*.
 *
 * Tout est pré-calculé côté ADM (commande `ef:dashboard:collect`, toutes les
 * 5 minutes) : l'écran relit la dernière photo et les séries stockées.
 */

export type WeatherState = 'sunny' | 'cloudy' | 'rainy' | 'stormy' | 'unknown';
export type Severity = 'critical' | 'warning' | 'info';
export type SectionStatus = 'ok' | 'error' | 'unconfigured';

export interface Weather {
  key: 'v1' | 'v2' | 'data' | 'infra' | 'ops';
  label: string;
  state: WeatherState;
  stateLabel: string;
  counts: { critical: number; warning: number; info: number };
  reasons: { severity: Severity; title: string }[];
}

export interface DashboardAlert {
  id?: number;
  fingerprint: string;
  severity: Severity;
  category: string;
  title: string;
  detail: string | null;
  value: number | null;
  firstSeenAt?: string;
  lastSeenAt?: string;
  resolvedAt?: string | null;
  occurrences?: number;
}

export interface Section<T> {
  status: SectionStatus;
  collectedAt: string;
  lastSuccessAt?: string | null;
  durationMs?: number;
  error?: string;
  reused?: boolean;
  data: T | null;
  metrics?: Record<string, number>;
}

export type RoleCounts = { student: number; parent: number; teacher: number; staff: number };
export type V2RoleCounts = RoleCounts & { thirdParty: number };

export interface InfraData {
  cluster: {
    nodesTotal: number;
    nodesReady: number;
    appCpuPct: number | null;
    appMemPct: number | null;
    cpuPctMax: number | null;
    memPctMax: number | null;
    diskPctMax: number | null;
    podsRunning: number;
    appPods: number;
    appPodCapacity: number;
  };
  nodes: {
    name: string;
    pool: string;
    cpuPct: number | null;
    memPct: number | null;
    memGb: number | null;
    diskPct: number | null;
    cores: number | null;
    ready: boolean;
    pods: number;
    podCapacity: number;
  }[];
  workloads: {
    key: string;
    label: string;
    platform: string;
    cpuCores: number;
    memBytes: number;
    pods: number;
    restarts24h: number;
  }[];
  problems: {
    pods: { namespace: string; pod: string; container?: string; reason: string }[];
    restarts1h: { namespace: string; pod: string; container: string; count: number }[];
    unavailable: { kind: string; namespace: string; name: string; missing: number; available: number | null }[];
  };
  cronjobs: {
    name: string;
    short: string;
    platform: string;
    schedule: string;
    intervalSec: number | null;
    lastSuccessAgoSec: number | null;
    active: number;
    suspended: boolean;
    failedLast24h: number;
    stale: boolean;
  }[];
}

export interface Upstream {
  key: string;
  label: string;
  platform: string;
  requests5m: number;
  rpm: number;
  errors5xx5m: number;
  errors4xx5m: number;
  errorRatePct: number;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
}

export interface Probe {
  key: string;
  label: string;
  platform: string;
  url: string;
  status: number | null;
  ok: boolean;
  latencyMs: number | null;
  error: string | null;
}

export interface ApisData {
  upstreams: Upstream[];
  probes: Probe[];
  topErrors: { service: string; method: string; route: string; status: number; count: number }[];
  slow: { service: string; method: string; route: string; count: number }[];
  appErrors: { container: string; label: string; platform: string; count5m: number; count1h: number }[];
  dataInternal: { requests5m: number; errors5xx5m: number; errorRatePct: number } | null;
  lokiConfigured: boolean;
  lokiError: string | null;
}

export interface QueuesData {
  transports: { name: string; messages: number; ready: number; unacked: number; consumed: boolean }[];
  others: { name: string; messages: number }[];
  failed: number;
  backlog: number;
  unconsumed: number;
}

export interface DatabasesData {
  mariadb: {
    error?: string;
    threadsConnected?: number;
    threadsRunning?: number;
    maxConnections?: number;
    maxUsedConnections?: number;
    uptimeSec?: number;
    qps?: number | null;
    slowPerMin?: number | null;
    sizeGb?: number | null;
    databases?: number | null;
  };
  postgres: {
    error?: string;
    connections?: { total: number; active: number; idle: number; idleInTransaction: number };
    maxConnections?: number;
    longestQuerySec?: number;
    tps?: number | null;
    cacheHitPct?: number | null;
    deadlocks?: number | null;
    replicas?: number | null;
    replicationLagSec?: number | null;
    databases?: number;
    sizeGb?: number | null;
  };
}

export interface V1Data {
  schoolsProduction: number;
  loginsToday: number;
  /** utilisateurs distincts ayant fait un login explicite (loginlog) */
  loginUsersToday?: number;
  /** actifs = lastlogin_date du jour (web + appli mobile) */
  activeUsersToday: number;
  activeByRole: RoleCounts;
  activeSchoolsToday: number;
  logins5m: number;
  online15m: number;
  hourly: number[];
  bySchool: {
    schoolId: number;
    code: string;
    name: string;
    logins: number;
    users: number;
    loginUsers?: number;
    byRole: RoleCounts;
    lastLoginAt: string | null;
  }[];
}

export interface V2Data {
  tenants: Record<string, number>;
  accountsTotal: number;
  accountsCreatedToday: number;
  loginsToday: number;
  activeUsersToday: number;
  online15m: number;
  /** comptes internes École Futée actifs (exclus des actifs) */
  internalActiveToday?: number;
  hourly: number[];
  schoolsEnteredToday: number;
  schoolEntriesToday: number;
  bySchool: { tenantId: string; schoolId: number | null; code: string; name: string; entries: number; lastAccessAt: string | null }[];
}

export type ActivityKey =
  | 'attendanceCalls'
  | 'grades'
  | 'evaluations'
  | 'textbookEntries'
  | 'homeworks'
  | 'messages'
  | 'announcements'
  | 'mails'
  | 'mailsFailed'
  | 'documents'
  | 'formSubmissions'
  | 'invoices';

export interface V2TenantsData {
  tenantsScanned: number;
  tenantsTotal: number;
  partial: boolean;
  errors: { code: string; error: string }[];
  errorCount: number;
  durationMs: number;
  totals: V2RoleCounts;
  activeToday: V2RoleCounts;
  newToday: V2RoleCounts;
  activity: Record<ActivityKey, number>;
  perTenant: {
    tenantId: string;
    schoolId: number | null;
    code: string;
    name: string;
    totals: V2RoleCounts;
    active: V2RoleCounts;
    activeTotal: number;
    new: V2RoleCounts;
    activity: Record<ActivityKey, number>;
    lastConnectionAt: string | null;
  }[];
}

export type SplitCategory = 'v1Only' | 'both' | 'v2Only' | 'none';

export interface PlatformData {
  fleet: {
    v1Production: number;
    v2Production: number;
    bothProduction: number;
    v1OnlyProduction: number;
    v2OnlyProduction: number;
    v1Demo: number;
    v2Demo: number;
    total: number;
  };
  rates: {
    fleetV2Pct: number | null;
    usageV2Pct: number | null;
    schoolsV2OnlyPct: number | null;
    /** écoles actives dont ≥ 80 % des actifs du jour sont en V2 */
    schoolsMostlyV2Pct?: number | null;
    schoolsMostlyV2?: number;
    schoolsActive?: number;
    v1Dau: number;
    v2Dau: number;
  };
  split: {
    counts: Record<SplitCategory, number>;
    schools: {
      schoolId: number;
      code: string;
      name: string;
      v1: string | null;
      v2: string | null;
      v1Users: number;
      v2Users: number;
      category: SplitCategory;
      v2SharePct: number | null;
    }[];
  };
  migration: {
    projects: Record<string, number>;
    projectsTotal: number;
    records: { total: number; processed: number; failed: number };
    steps: Record<string, number>;
    stepsFailed: number;
    recent: {
      code: string;
      name: string;
      status: string;
      completedAt: string | null;
      startedAt: string | null;
      plannedAt: string | null;
      processed: number;
      total: number;
      failed: number;
    }[];
    weeklyCompleted: { week: string; count: number }[];
  };
  ops: {
    backups: { v1: number; v2: number };
    metricsLastRun: Record<string, string>;
    schoolYear: { code: string; starting_date: string; ending_date: string } | null;
    autoCatchUp: {
      status: string;
      trigger_source: string;
      started_at: string;
      finished_at: string | null;
      projects_scanned: number;
      projects_in_error: number;
      added_records: number;
      failed_records: number;
    } | null;
  };
}

export interface DashboardPayload {
  version: number;
  generatedAt: string;
  durationMs: number;
  weather: Record<Weather['key'], Weather>;
  alerts: DashboardAlert[];
  sections: {
    infra?: Section<InfraData>;
    apis?: Section<ApisData>;
    queues?: Section<QueuesData>;
    databases?: Section<DatabasesData>;
    v1?: Section<V1Data>;
    v2?: Section<V2Data>;
    v2Tenants?: Section<V2TenantsData>;
    platform?: Section<PlatformData>;
  };
}

export interface SnapshotMeta {
  id: number;
  capturedAt: string;
  day: string;
  isDayClose: boolean;
  durationMs: number;
}

export interface CurrentResponse {
  snapshot: SnapshotMeta | null;
  payload: DashboardPayload | null;
}

export interface DayResponse extends CurrentResponse {
  day: string;
  daily: Record<string, { v: number; s: string }>;
}

export type SeriesPoint = { t: string; v: number };
export type DailyPoint = { d: string; v: number; s: string };

/** null = inchangé (304) */
export async function getCurrent(since?: number): Promise<CurrentResponse | null> {
  const res = await apiClient.get<CurrentResponse>('/api/dashboard-live/current', {
    params: since ? { since } : undefined,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
  });
  return res.status === 304 ? null : res.data;
}

export async function getDay(day: string): Promise<DayResponse> {
  const res = await apiClient.get<DayResponse>(`/api/dashboard-live/day/${day}`);
  return res.data;
}

export async function getDays(): Promise<string[]> {
  const res = await apiClient.get<{ days: string[] }>('/api/dashboard-live/days');
  return res.data.days;
}

export async function getSeries(
  metrics: string[],
  opts: { range?: string; from?: string; to?: string } = {}
): Promise<Record<string, SeriesPoint[]>> {
  const res = await apiClient.get<{ series: Record<string, SeriesPoint[]> }>('/api/dashboard-live/series', {
    params: { metrics: metrics.join(','), ...opts },
  });
  return res.data.series;
}

export async function getDaily(
  metrics: string[],
  opts: { days?: number; from?: string; to?: string } = {}
): Promise<Record<string, DailyPoint[]>> {
  const res = await apiClient.get<{ series: Record<string, DailyPoint[]> }>('/api/dashboard-live/daily', {
    params: { metrics: metrics.join(','), ...opts },
  });
  return res.data.series;
}

export async function getAlerts(days = 7, to?: string): Promise<{ open: DashboardAlert[]; history: DashboardAlert[] }> {
  const res = await apiClient.get('/api/dashboard-live/alerts', { params: { days, to } });
  return res.data;
}
